import React, { useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { GET_ETSY_LISTING_STATS_BY_STORE } from "../../graphql/queries/etsy-metric";
import moment from "moment";
import styled from "styled-components";
import { Badge, Banner, Pagination, SkeletonBodyText } from "@shopify/polaris";
import UpdatedAtTooltip from "./updated-at-tooltip";
import { formatMetricValue } from '../../helper';

const PAGE_SIZE = 20;
const ADS_COLUMNS = [
    { key: "isPromoted", label: "Ad on/off", format: "boolean" },
    { key: "impressionCount", label: "Views" },
    { key: "clickCount", label: "Clicks" },
    { key: "clickRate", label: "Click rate", format: "percent" },
    { key: "conversions", label: "Orders" },
    { key: "revenue", label: "Revenue", format: "currency" },
    { key: "spentTotal", label: "Spend", format: "currency" },
    { key: "roas", label: "ROAS" },
];
const STATS_COLUMNS = [
    { key: "views", label: "Views" },
    { key: "favorites", label: "Favorites" },
    { key: "orders", label: "Orders" },
    { key: "revenue", label: "Revenue", format: "currency" },
];

const AGG_COLUMNS = [
    { key: "adsPercentage", label: "Ads %", format: "percent" },
    { key: "trueRoas", label: "True ROAS" },
];

function formatListingValue({ value, format, currency }) {
    if (value == null) return "—";
    if (format === "boolean") return value ? "Yes" : "No";

    const isCurrency = format === "currency";
    const formatted = formatMetricValue({ value, isCurrency, currency });

    // const formatted = value.toLocaleString("en-US", {
    //     maximumFractionDigits: 2,
    //     ...(format === "currency"
    //         ? { style: "currency", currency: "USD" }
    //         : {}),
    // });
    return format === "percent" ? `${formatted}%` : formatted;
}

const ListingSection = styled.section`
    padding: 1.6rem 2rem;
    border-top: 1px solid #dfe3e8;

    .listing-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1.2rem;
        margin-bottom: 1.2rem;
    }

    h3 {
        margin: 0;
        font-weight: 600;
    }

    .listing-table-scroll {
        overflow-x: scroll;
        isolation: isolate;

        &::-webkit-scrollbar {
            height: 16px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f2f3;
        }

        &::-webkit-scrollbar-thumb {
            border: 3px solid #f1f2f3;
            border-radius: 8px;
            background: #637381;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #454f5b;
        }

        @supports not selector(::-webkit-scrollbar) {
            scrollbar-width: auto;
            scrollbar-color: #637381 #f1f2f3;
        }

        &:focus-visible {
            outline: 2px solid #5c6ac4;
            outline-offset: -2px;
        }
    }

    table {
        width: 100%;
        min-width: 168rem;
        table-layout: auto;
        border-collapse: separate;
        border-spacing: 0;
        font-variant-numeric: tabular-nums;
    }

    th,
    td {
        padding: 1.2rem;
        border-bottom: 1px solid #dfe3e8;
        text-align: right;
        white-space: nowrap;
    }

    .listing-column {
        width: 50rem;
    }

    .listing-cell {
        position: sticky;
        left: 0;
        z-index: 2;
        text-align: center;
        background: #f4f6f8;
    }

    .listing-cell::after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 1px;
        background: #f7f7f7;
        box-shadow: 4px 0 8px 1px rgba(33, 43, 54, 0.16);
        pointer-events: none;
    }

    .column-group {
        text-align: center;
        background: #f4f6f8;
    }

    .bl {
        border-left: 2px solid #dfe3e8;
    }

    .listing-product {
        display: flex;
        align-items: center;
        justify-content: start;
        gap: 1.2rem;
        min-width: 0;
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .listing-product img {
        width: 48px;
        height: 48px;
        flex-shrink: 0;
        object-fit: cover;
        border-radius: 4px;
    }

    .listing-id {
        display: block;
        color: #637381;
        font-size: 1.2rem;
    }
`;

export default function EtsyListingStats({ storeId, dateRange, storeName }) {
    const [offset, setOffset] = useState(0);
    const { data, loading, error, refetch } = useQuery(
        GET_ETSY_LISTING_STATS_BY_STORE,
        {
            variables: {
                filter: {
                    storeIds: [storeId],
                    dateRange,
                    limit: PAGE_SIZE,
                    offset,
                },
            },
            fetchPolicy: "no-cache",
            notifyOnNetworkStatusChange: true,
        },
    );
    const group =
        data &&
        data.getEtsyListingStatsByStore.find(
            (item) => item.store.id === storeId,
        );
    const rows = group ? group.data : [];
    const currency = group ? group.currency : "";

    return (
        <ListingSection aria-label={`Listing stats for ${storeName}`}>
            <div className="listing-header">
                <h3>Listing stats</h3>
                <UpdatedAtTooltip
                    updatedAt={
                        !loading && !error && group ? group.updatedAt : null
                    }
                />
            </div>
            <p style={{ marginBottom: "1.2rem" }}>
                {moment(dateRange.from).format("DD-MM-YYYY")} –{" "}
                {moment(dateRange.to).format("DD-MM-YYYY")}
            </p>
            {loading ? (
                <div role="status" aria-label="Loading listing stats">
                    <SkeletonBodyText lines={5} />
                </div>
            ) : error ? (
                <Banner
                    status="critical"
                    title="Could not load listing stats"
                    action={{ content: "Retry", onAction: () => refetch() }}
                >
                    <p>{error.message}</p>
                </Banner>
            ) : rows.length ? (
                <div
                    className="listing-table-scroll"
                    tabIndex={0}
                    role="region"
                    aria-label={`Scrollable listing stats for ${storeName}`}
                >
                    <table aria-label={`Listing stats for ${storeName}`}>
                        <colgroup>
                            <col className="listing-column" />
                            {ADS_COLUMNS.map(({ key }) => (
                                <col key={`ads-${key}`} />
                            ))}
                            {STATS_COLUMNS.map(({ key }) => (
                                <col key={`stats-${key}`} />
                            ))}
                            {AGG_COLUMNS.map(({ key }) => (
                                <col key={`aggs-${key}`} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    rowSpan={2}
                                    className="listing-cell"
                                >
                                    Listing
                                </th>
                                <th
                                    scope="colgroup"
                                    colSpan={ADS_COLUMNS.length}
                                    className="column-group"
                                >
                                    Ads
                                </th>
                                <th
                                    scope="colgroup"
                                    colSpan={STATS_COLUMNS.length}
                                    className="column-group bl"
                                >
                                    Stats
                                </th>
                                <th
                                    scope="colgroup"
                                    colSpan={AGG_COLUMNS.length}
                                    className="column-group bl"
                                ></th>
                            </tr>
                            <tr>
                                {ADS_COLUMNS.map(({ key, label }) => (
                                    <th scope="col" key={`ads-${key}`}>
                                        {label}
                                    </th>
                                ))}
                                {STATS_COLUMNS.map(({ key, label }, index) => (
                                    <th
                                        scope="col"
                                        key={`stats-${key}`}
                                        className={
                                            index === 0
                                                ? // ? "stats-start"
                                                  "bl"
                                                : undefined
                                        }
                                    >
                                        {label}
                                    </th>
                                ))}
                                {AGG_COLUMNS.map(({ key, label }, index) => (
                                    <th
                                        scope="col"
                                        key={`agg-${key}`}
                                        className={
                                            index === 0 ? "bl" : undefined
                                        }
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id}>
                                    <td className="listing-cell">
                                        <div className="listing-product">
                                            {row.imageLink && (
                                                <img
                                                    src={row.imageLink}
                                                    alt=""
                                                    loading="lazy"
                                                />
                                            )}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "start",
                                                    textAlign: "left",
                                                    gap: "1rem",
                                                }}
                                            >
                                                <p
                                                    data-listing-id={
                                                        row.listingId
                                                    }
                                                >
                                                    {row.listingName ||
                                                        row.listingId}
                                                </p>
                                                {/* <span className="listing-id">{row.listingId}</span> */}
                                                <div>
                                                    <Badge
                                                        children={row.status}
                                                        status={
                                                            row.status ===
                                                            "Active"
                                                                ? "warning"
                                                                : "new"
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    {ADS_COLUMNS.map(({ key, format }) => (
                                        <td key={`ads-${key}`}>
                                            {formatListingValue({
                                                value: row.ads
                                                    ? row.ads[key]
                                                    : null,
                                                format,
                                                currency,
                                            })}
                                        </td>
                                    ))}
                                    {STATS_COLUMNS.map(
                                        ({ key, format }, index) => (
                                            <td
                                                key={`stats-${key}`}
                                                className={
                                                    index === 0
                                                        ? // ? "stats-start"
                                                          "bl"
                                                        : undefined
                                                }
                                            >
                                                {formatListingValue({
                                                    value: row[key],
                                                    format,
                                                    currency,
                                                })}
                                            </td>
                                        ),
                                    )}
                                    {AGG_COLUMNS.map(
                                        ({ key, format }, index) => (
                                            <td
                                                key={`agg-${key}`}
                                                className={
                                                    index === 0
                                                        ? "bl"
                                                        : undefined
                                                }
                                            >
                                                {formatListingValue({
                                                    value: row.agg
                                                        ? row.agg[key]
                                                        : null,
                                                    format,
                                                    currency,
                                                })}
                                            </td>
                                        ),
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p>No listing stats found for this period.</p>
            )}
            <div style={{ marginTop: "1.6rem" }}>
                <Pagination
                    hasPrevious={!loading && offset > 0}
                    onPrevious={() =>
                        setOffset((value) => Math.max(0, value - PAGE_SIZE))
                    }
                    hasNext={!loading && !error && rows.length === PAGE_SIZE}
                    onNext={() => setOffset((value) => value + PAGE_SIZE)}
                />
            </div>
        </ListingSection>
    );
}
